<!-- 
  LazyImg.vue
  A Vue component that implements image lazy loading using both native lazy loading
  and content-visibility for optimal performance
-->
<template>
  <img
    ref="img"
    :src="loadedSrc"
    :data-src="src"
    :alt="alt"
    loading="lazy"
    class="lazy-img"
    :class="[
      {
        'lazy-img--loaded': isLoaded,
        'lazy-img--visibility': useVisibility,
      },
      $attrs.class,
    ]"
    v-bind="$attrs"
    @load="onImageLoaded"
    @error="onImageError"
  />
</template>

<script>
import { setupLazyImage } from '@/utils/lazyImage.js'

export default {
  name: "LazyImg",
  inheritAttrs: false,
  props: {
    src: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: "",
    },
    compatibility: {
      type: Boolean,
      default: true,
      description: "是否启用 IntersectionObserver 来提升兼容性",
    },
    useVisibility: {
      type: Boolean,
      default: false,
      description: "是否启用 content-visibility 优化",
    },
  },
  data() {
    return {
      loadedSrc: "",
      isLoaded: false,
      observer: null,
    };
  },
  mounted() {
    if (this.compatibility && "IntersectionObserver" in window) {
      this.observer = setupLazyImage(this.$refs.img, () => {
        this.loadImage();
      });
    } else {
      console.log("不支持 IntersectionObserver");
      this.loadImage();
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    /**
     * 设置 Intersection Observer 来实现图片懒加载
     * 当图片进入视口时才开始加载，提升页面性能
     */
    setupIntersectionObserver() {
      // 创建 IntersectionObserver 实例
      this.observer = new IntersectionObserver(
        // 回调函数，当被观察元素的可见性变化时触发
        (entries) => {
          // entries 是被观察的元素集合，这里只观察一个元素，所以直接取第一个
          const [entry] = entries;

          // 检查元素是否在视口内或接近视口
          if (entry.isIntersecting) {
            this.loadImage();
            this.observer.disconnect();
          }
        },
        {
          // 配置选项
          threshold: [0.1], // 当元素出现10%时都触发
          rootMargin: "50px 1000px",
        }
      );

      // 开始观察图片元素
      this.observer.observe(this.$refs.img);
    },
    loadImage() { 
      this.loadedSrc = this.src;
      // console.log("loadImage:", this.src);
    },
    onImageLoaded() {
      this.isLoaded = true;
      this.$emit("loaded");
    },
    onImageError(error) {
      this.$emit("error", error);
    },
  },
};
</script>

<style scoped>
.lazy-img {
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.lazy-img--loaded {
  opacity: 1;
}

.lazy-img--visibility {
  content-visibility: auto;
}
</style>
